import { green, grey, red, yellow } from '@mercedes-benz/mui5-theme';
import { Dispatch, JSX, SetStateAction } from 'react';
import { Accept } from 'react-dropzone';
import { PrimitiveType } from 'react-intl';
import { QUERY_KEY } from '../../api/cachedDataHooks';
import {
    AuthorizedSignatoryDto,
    CompanyRegistrationCheckStepDto,
    CompanyRegistrationDto,
    ExtendedProductApprovalDto,
    OperatingFluidTypeDto,
    ProductApprovalDto,
    ProductFormulationDto,
    ProductSheetDto,
    UserDto,
    VersionedFileMetadataDto,
} from '../api';
import { ExtendedCompanyRegistrationDto } from '../api/models/ExtendedCompanyRegistrationDto';
import I18nTexts from '../common/i18n/I18nTexts';
import { SupportedLanguages } from '../common/i18n/SupportedLanguages';
import { Translate } from '../common/i18n/useTranslation';
import { StateFilterEnum } from '../components/admin/client/utils/StateFilterEnum';
import { FileBase46 } from '../components/admin/testAggregates/oilTestingEngineTests/oilTestingEngineTests/tabs/oilTestingEngineTestDetail/FilesUploadBase64';
import { Routes } from '../routes/routes';
import {
    AdminRegistrationStepEnum,
    CheckColor,
    EngineOil,
    FileType,
    GearOil,
    ProductStateMapping,
    VerificationExistingCheckEnum,
} from './enums';
import { CustomStepperIcon, FetchedFile } from './interfaces';
import { initialPagination } from './pagination';
import { DE_ALL_NUMBER_FORMAT, DE_NUMBER_FORMAT, EN_ALL_NUMBER_FORMAT, EN_NUMBER_FORMAT } from './regexPatterns';
import { TableConfig } from '../common/components/dataDisplay/dataGrid/BeamDataGridGenericOverview';

// Re-export from userStorage to maintain backwards compatibility
export { getEntitlementGroup, getLocallyStoredUser, setLocallyStoredUser } from './userStorage';

export enum MaxUploadFileSizes {
    MB5 = 1024 * 1024 * 5,
    MB10 = 1024 * 1024 * 10,
    MB20 = 1024 * 1024 * 20,
}

export const convertByteToMb = (byte: number) => {
    return byte / 1024 / 1024;
};

export const convertMbToByte = (mb: number) => {
    return mb * 1024 * 1024;
};

export const MINUTE_IN_MS = 60000;
export const TABLE_MIN_HEIGHT = 500; //500px

export const getTableHeight = (currentHeight: number) => {
    //ensure that the table height has a good minimum height
    return currentHeight < TABLE_MIN_HEIGHT ? TABLE_MIN_HEIGHT : currentHeight;
};

export const transformInformation = (
    authInfo: CompanyRegistrationDto | undefined,
    state?: CompanyRegistrationDto.registrationState
): CompanyRegistrationDto => {
    return {
        authorizedSignatory: authInfo?.authorizedSignatory ?? '',
        registrationState: state ?? (authInfo?.registrationState as CompanyRegistrationDto.registrationState),
        termsOfUseApproved: authInfo?.termsOfUseApproved ?? false,
        sendTermsOfUseMail: authInfo?.sendTermsOfUseMail ?? '',
        taxId: authInfo?.taxId ?? '',
        website: authInfo?.website ?? '',
        admin: authInfo?.admin,
        modifiedDate: authInfo?.modifiedDate ?? '',
        version: authInfo?.version ?? 0,
    } as CompanyRegistrationDto;
};

export const getCheckStateStatus = (verification: VerificationExistingCheckEnum) => {
    switch (verification) {
        case VerificationExistingCheckEnum.IN_PROGRESS:
            return CompanyRegistrationCheckStepDto.checkStepState.IN_PROGRESS;
        case VerificationExistingCheckEnum.ACCEPTED:
            return CompanyRegistrationCheckStepDto.checkStepState.ACCEPTED;
        case VerificationExistingCheckEnum.REJECTED:
            return CompanyRegistrationCheckStepDto.checkStepState.REJECTED;
        case VerificationExistingCheckEnum.NOT_AVAILABLE:
            return CompanyRegistrationCheckStepDto.checkStepState.NOT_AVAILABLE;
        default:
            return CompanyRegistrationCheckStepDto.checkStepState.NOT_AVAILABLE;
    }
};

export const handleStatusCheck = (
    value: VerificationExistingCheckEnum,
    updateCustomIcons: (index: number, state: CompanyRegistrationCheckStepDto.checkStepState) => void,
    index: number
) => {
    updateCustomIcons(index, getCheckStateStatus(value));
};

export const getCheckStepFromRegistration = (
    companyRegStatus: CompanyRegistrationDto.registrationState
): CompanyRegistrationCheckStepDto.checkStepState => {
    switch (companyRegStatus) {
        case CompanyRegistrationDto.registrationState.IN_PROGRESS:
        case CompanyRegistrationDto.registrationState.IN_APPROVAL:
        case CompanyRegistrationDto.registrationState.CREATE_INVOICE:
        case CompanyRegistrationDto.registrationState.CHECK_COMPANY_DATA:
        case CompanyRegistrationDto.registrationState.UPLOAD_INVOICE:
        case CompanyRegistrationDto.registrationState.ACTIVATE:
            return CompanyRegistrationCheckStepDto.checkStepState.IN_PROGRESS;
        case CompanyRegistrationDto.registrationState.APPROVED:
            return CompanyRegistrationCheckStepDto.checkStepState.ACCEPTED;
        case CompanyRegistrationDto.registrationState.REJECTED:
            return CompanyRegistrationCheckStepDto.checkStepState.REJECTED;

        default:
            return CompanyRegistrationCheckStepDto.checkStepState.NOT_AVAILABLE;
    }
};

export const getCheckedStatus = (verification: CheckColor) => {
    switch (verification) {
        case CheckColor.ACCEPTED:
            return VerificationExistingCheckEnum.ACCEPTED;
        case CheckColor.IN_PROGRESS:
            return VerificationExistingCheckEnum.IN_PROGRESS;
        case CheckColor.NONE:
            return VerificationExistingCheckEnum.NOT_AVAILABLE;
        case CheckColor.REJECTED:
            return VerificationExistingCheckEnum.REJECTED;
        default:
            return VerificationExistingCheckEnum.NOT_AVAILABLE;
    }
};

export const getSortedIconsArray = (
    stepInformationCollection: CompanyRegistrationCheckStepDto[],
    getColoredIcon: (state: CompanyRegistrationCheckStepDto.checkStepState | undefined) => {
        icon: JSX.Element;
        color: CheckColor;
    }
) => {
    const arr: CustomStepperIcon[] = [];

    stepInformationCollection?.map((step: CompanyRegistrationCheckStepDto) => {
        const { icon, color } = getColoredIcon(step.checkStepState);
        switch (step.checkStepType) {
            case CompanyRegistrationCheckStepDto.checkStepType.PLAUSIBILITY_FLUID_FINDER:
                arr.push({ icon: icon, color: color, stepIndex: AdminRegistrationStepEnum.PLAUSIBILITY_FLUID_FINDER });
                break;
            case CompanyRegistrationCheckStepDto.checkStepType.CLAIM:
                arr.push({ icon: icon, color: color, stepIndex: AdminRegistrationStepEnum.OFFER_OF_A_REWARD });
                break;
            case CompanyRegistrationCheckStepDto.checkStepType.INFO_LETTER:
                arr.push({ icon: icon, color: color, stepIndex: AdminRegistrationStepEnum.INFO_LETTER });
                break;
            case CompanyRegistrationCheckStepDto.checkStepType.INVOICE:
                arr.push({ icon: icon, color: color, stepIndex: AdminRegistrationStepEnum.INVOICE });
                break;
            default:
                arr.push({ icon: icon, color: color, stepIndex: AdminRegistrationStepEnum.ACTIVATION });
                break;
        }
    });
    arr.sort((a, b) => a.stepIndex - b.stepIndex);
    return arr;
};

export function getUniqueArrayByProperty<T>(arr: T[], propertyName: keyof T): T[] {
    const uniqueMap = new Map<T[keyof T], T>();

    arr.forEach((item) => {
        const key = item[propertyName];
        if (!uniqueMap.has(key)) {
            uniqueMap.set(key, item);
        }
    });

    return Array.from(uniqueMap.values());
}

export function updateEntryByCheckStepType(
    array: CompanyRegistrationCheckStepDto[],
    type: CompanyRegistrationCheckStepDto.checkStepType | 'ACTIVATION',
    updatedEntry: Partial<CompanyRegistrationCheckStepDto>
): CompanyRegistrationCheckStepDto[] {
    const entryToManipulate = array.find((item) => item.checkStepType === type);
    if (entryToManipulate) {
        Object.assign(entryToManipulate, updatedEntry);
    }
    return array;
}

export function sortStepsByCheckStepType(array: CompanyRegistrationCheckStepDto[]): CompanyRegistrationCheckStepDto[] {
    const order: { [x: string]: number } = {
        [CompanyRegistrationCheckStepDto.checkStepType.PLAUSIBILITY_FLUID_FINDER]: 1,
        [CompanyRegistrationCheckStepDto.checkStepType.CLAIM]: 2,
        [CompanyRegistrationCheckStepDto.checkStepType.INFO_LETTER]: 3,
        [CompanyRegistrationCheckStepDto.checkStepType.INVOICE]: 4,
        ['ACTIVATION']: 5,
    };

    return array.sort((step1, step2) => {
        const type1 = step1.checkStepType as CompanyRegistrationCheckStepDto.checkStepType;
        const type2 = step2.checkStepType as CompanyRegistrationCheckStepDto.checkStepType;

        return order[type1] - order[type2];
    });
}

export const updateFetchedRegistrationCheckSteps = (
    result: CompanyRegistrationCheckStepDto[],
    getColoredIcon: (state: CompanyRegistrationCheckStepDto.checkStepState | undefined) => {
        icon: JSX.Element;
        color: CheckColor;
    }
) => {
    // const unique = getUniqueArrayByProperty(result as CompanyRegistrationCheckStepDto[], 'checkStepType');
    const sortedUnique = sortStepsByCheckStepType(result);
    const sortedIcons = getSortedIconsArray(result, getColoredIcon);

    return { sortedIcons, sortedUnique };
};

export function updateIconInArray(
    array: CustomStepperIcon[],
    targetIndex: number,
    newIcon: JSX.Element,
    color: CheckColor
): CustomStepperIcon[] {
    return array.map((item) => (item.stepIndex === targetIndex ? { ...item, icon: newIcon, color: color } : item));
}

const getCheckStepState = (color: CheckColor): CompanyRegistrationCheckStepDto.checkStepState => {
    switch (color) {
        case CheckColor.ACCEPTED:
            return CompanyRegistrationCheckStepDto.checkStepState.ACCEPTED;
        case CheckColor.IN_PROGRESS:
            return CompanyRegistrationCheckStepDto.checkStepState.IN_PROGRESS;
        case CheckColor.REJECTED:
            return CompanyRegistrationCheckStepDto.checkStepState.REJECTED;
        default:
            return CompanyRegistrationCheckStepDto.checkStepState.NOT_AVAILABLE;
    }
};

export const updateCustomIconsAndSteps = (
    index: AdminRegistrationStepEnum,
    state: CompanyRegistrationCheckStepDto.checkStepState,
    customIcons: CustomStepperIcon[],
    getColoredIcon: (state: CompanyRegistrationCheckStepDto.checkStepState | undefined) => {
        icon: JSX.Element;
        color: CheckColor;
    },
    companyRegistrationsCheckSteps: CompanyRegistrationCheckStepDto[],
    setCompanyRegistrationsCheckSteps: React.Dispatch<React.SetStateAction<CompanyRegistrationCheckStepDto[]>>,
    setCustomIcons: (value: React.SetStateAction<CustomStepperIcon[]>) => void
) => {
    if (customIcons.findIndex((i) => i.stepIndex === index) !== -1) {
        const foundIconIndex = customIcons.findIndex((i) => i.stepIndex === index);
        const { icon, color } = getColoredIcon(state);
        const updatedArray = updateIconInArray(customIcons, foundIconIndex, icon, color);
        const updatedSteps = companyRegistrationsCheckSteps?.map((step, idx) => {
            if (idx === foundIconIndex) {
                return {
                    ...step,
                    checkStepState: getCheckStepState(color),
                };
            } else {
                return step;
            }
        });
        setCustomIcons([...updatedArray]);
        setCompanyRegistrationsCheckSteps(updatedSteps);
    }
};

export const setChildRoute = (
    entryName: string,
    childText: string,
    translate: (textKey: keyof I18nTexts, paramsObj?: Record<string, PrimitiveType> | undefined) => string,
    navigateTo: (route: Routes) => void
) => {
    switch (entryName) {
        case translate('dashboard'):
            return;

        case translate('client'):
            switch (childText) {
                case translate('beam_registrations'):
                    navigateTo(Routes.admin_client_registrations_details);
                    return;
                case translate('change_client_data'):
                    navigateTo(Routes.admin_client_registrations_change);
                    return;
                case translate('manage_client_data'):
                    navigateTo(Routes.admin_client_registrations_manage);
                    return;
                case translate('invoices_name_plural'):
                    navigateTo(Routes.admin_client_invoices);
                    return;
            }
            return;

        case translate('products'):
            switch (childText) {
                case translate('products_overview'):
                    navigateTo(Routes.admin_products_overview);
                    return;
                case translate('processes_overview'):
                    navigateTo(Routes.admin_processes_overview);
                    return;
            }
            return;

        case translate('master_data'):
            switch (childText) {
                case translate('sheet_care'):
                    navigateTo(Routes.admin_master_data_sheet_care);
                    return;
                case translate('product_care'):
                    navigateTo(Routes.admin_master_data_product_care);
                    return;
                case translate('area_care'):
                    navigateTo(Routes.admin_master_data_area_care);
                    return;
                case translate('test_care'):
                    navigateTo(Routes.admin_master_data_test_care);
                    return;
                case translate('additiv_baseoil_care'):
                    navigateTo(Routes.admin_master_data_additive_baseoil_care);
                    return;
            }
            return;

        case translate('test_aggregates'):
            switch (childText) {
                case translate('oil_testing_engine'):
                    navigateTo(Routes.admin_test_aggregates_oil_testing_engine_tests);
                    return;
                case translate('oil_testing_engines_master_data'):
                    navigateTo(Routes.admin_test_aggregates_oil_testing_engine_master_data);
                    return;
            }
            return;

        case translate('external_applications'):
            switch (childText) {
                case translate('operating_fluid_website'):
                    navigateTo(Routes.admin_external_applications_operating_fluid_website);
                    return;
                case translate('bmaterial_api'):
                    navigateTo(Routes.admin_external_applications_bmaterial_api);
                    return;
                case translate('fluidfinder'):
                    navigateTo(Routes.admin_external_applications_fluidfinder);
                    return;
                case translate('fuels_db'):
                    navigateTo(Routes.admin_external_applications_fuels_db);
                    return;
            }
            return;

        case translate('management'):
            switch (childText) {
                case translate('beam_news_manage'):
                    navigateTo(Routes.admin_management_news);
                    return;
                case translate('maintain_service_area'):
                    navigateTo(Routes.admin_management_service_area);
                    return;
                default:
                    navigateTo(Routes.admin_management_faq);
                    return;
            }
    }
};

export const ONLY_NUMBERS_REGEX = /(\D*)/;

export function getCheckStepStateLabel(
    state: CompanyRegistrationCheckStepDto.checkStepState,
    translate: Translate
): string {
    switch (state) {
        case CompanyRegistrationCheckStepDto.checkStepState.ACCEPTED:
            return translate('approved_registrations');
        case CompanyRegistrationCheckStepDto.checkStepState.IN_PROGRESS:
            return translate('in_progress');
        case CompanyRegistrationCheckStepDto.checkStepState.REJECTED:
            return translate('rejected');
        case CompanyRegistrationCheckStepDto.checkStepState.NOT_AVAILABLE:
            return VerificationExistingCheckEnum.NOT_AVAILABLE;
        default:
            return VerificationExistingCheckEnum.NOT_AVAILABLE;
    }
}

export function getRegistrationStateLabel(
    state: CompanyRegistrationDto.registrationState,
    translate: Translate
): string {
    switch (state) {
        case CompanyRegistrationDto.registrationState.APPROVED:
            return translate('approved_registrations');
        case CompanyRegistrationDto.registrationState.IN_PROGRESS:
            return translate('in_progress');
        case CompanyRegistrationDto.registrationState.REJECTED:
            return translate('rejected');
        case CompanyRegistrationDto.registrationState.PENDING:
            return 'AUSSTEHEND';
        case CompanyRegistrationDto.registrationState.IN_APPROVAL:
            return VerificationExistingCheckEnum.NOT_AVAILABLE;
        default:
            return VerificationExistingCheckEnum.NOT_AVAILABLE;
    }
}

export function getCheckStepColor(
    state: CompanyRegistrationCheckStepDto.checkStepState | undefined
): string | undefined {
    switch (state) {
        case CompanyRegistrationCheckStepDto.checkStepState.ACCEPTED:
            return green[50];
        case CompanyRegistrationCheckStepDto.checkStepState.IN_PROGRESS:
            return yellow[55];
        case CompanyRegistrationCheckStepDto.checkStepState.REJECTED:
            return red[45];
        case CompanyRegistrationCheckStepDto.checkStepState.NOT_AVAILABLE:
            return grey[80];
        default:
            return grey[80];
    }
}

export function getRegistrationChipColor(
    state: CompanyRegistrationDto.registrationState | undefined
): string | undefined {
    switch (state) {
        case CompanyRegistrationDto.registrationState.APPROVED:
            return green[50];
        case CompanyRegistrationDto.registrationState.IN_PROGRESS:
            return yellow[55];
        case CompanyRegistrationDto.registrationState.REJECTED:
            return red[45];
        case CompanyRegistrationDto.registrationState.PENDING:
            return yellow[25];
        case CompanyRegistrationDto.registrationState.IN_APPROVAL:
            return grey[80];
        default:
            return grey[80];
    }
}

export function getStepChipColorFromState(
    state: CompanyRegistrationCheckStepDto.checkStepState | undefined
): 'white' | 'black' | undefined {
    switch (state) {
        case CompanyRegistrationCheckStepDto.checkStepState.IN_PROGRESS:
        case CompanyRegistrationCheckStepDto.checkStepState.NOT_AVAILABLE:
        case undefined:
            return 'black';
        default:
            return 'white';
    }
}

export function getRegistrationChipColorFromState(
    state: CompanyRegistrationDto.registrationState | undefined
): 'white' | 'black' | undefined {
    switch (state) {
        case CompanyRegistrationDto.registrationState.IN_PROGRESS:
        case CompanyRegistrationDto.registrationState.PENDING:
        case CompanyRegistrationDto.registrationState.IN_APPROVAL:
        case undefined:
            return 'black';
        default:
            return 'white';
    }
}

export const getInfoLetterName = (id: string, files: FetchedFile[]) => {
    const infoFile = files.find((letter) => letter.id === id);
    return infoFile?.originalName;
};

export const getSalutation = (
    salutation: string | AuthorizedSignatoryDto.salutation | UserDto.salutation,
    translate: Translate
) => {
    switch (salutation) {
        case AuthorizedSignatoryDto.salutation.MR:
            return translate('salutation_mr');
        case AuthorizedSignatoryDto.salutation.MRS:
            return translate('salutation_mrs');
        default:
            return translate('salutation_other');
    }
};

export const formatCurrency = (value?: number | string, translate?: Translate, langState?: SupportedLanguages) => {
    if (value === undefined) {
        return '';
    }

    const numericValue = typeof value === 'string' ? parseFloat(value) : value;

    if (numericValue === 0 && translate) {
        return translate('for_free');
    }

    const formatter = new Intl.NumberFormat(langState ?? 'de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
    });

    return formatter.format(numericValue);
};

export const formatNumberByLocal = (
    value: number,
    langState: SupportedLanguages,
    minDigits?: number,
    maxDigits?: number
) => {
    const formatter = new Intl.NumberFormat(langState ?? 'de-DE', {
        style: 'decimal',
        minimumFractionDigits: minDigits ?? 0,
        maximumFractionDigits: maxDigits ?? 20,
    });
    return formatter.format(value);
};

export const getNumberFormatRegexByLocal = (langState: SupportedLanguages) => {
    return langState === SupportedLanguages.de ? DE_NUMBER_FORMAT : EN_NUMBER_FORMAT;
};
export const getAllNumberFormatRegexByLocal = (langState: SupportedLanguages) => {
    return langState === SupportedLanguages.de ? DE_ALL_NUMBER_FORMAT : EN_ALL_NUMBER_FORMAT;
};

export const getStatesToSearch = (
    state: ExtendedCompanyRegistrationDto.registrationState,
    statusFilter: string
): ExtendedCompanyRegistrationDto.registrationState[] => {
    const allStates = [
        StateFilterEnum.IN_PROGRESS,
        StateFilterEnum.IN_APPROVAL,
        StateFilterEnum.REJECTED,
        StateFilterEnum.UPLOAD_INVOICE,
        StateFilterEnum.CREATE_INVOICE,
        StateFilterEnum.CHECK_COMPANY_DATA,
        StateFilterEnum.ACTIVATE,
    ];
    const allInPogressStates = [
        StateFilterEnum.IN_PROGRESS,
        StateFilterEnum.UPLOAD_INVOICE,
        StateFilterEnum.CREATE_INVOICE,
        StateFilterEnum.CHECK_COMPANY_DATA,
        StateFilterEnum.ACTIVATE,
    ];
    if (state === StateFilterEnum.NONE) {
        return allStates;
    }
    if (state === StateFilterEnum.IN_PROGRESS) {
        return allInPogressStates;
    }
    // If the statusFilter matches any of the possible states, return only that one
    if (Object.values(StateFilterEnum).includes(statusFilter)) {
        return [statusFilter as ExtendedCompanyRegistrationDto.registrationState];
    }

    // Otherwise, return all possible states
    return allStates;
};

export const sortStringArrayAscending = (arr: string[]) => {
    return arr.sort((a, b) => (a.toUpperCase() > b.toUpperCase() ? 1 : -1));
};

export const createNewObjectWithoutEmptyStringFields = (obj: object) => {
    const postObject = Object.assign({}, obj);
    for (const [key, value] of Object.entries(postObject)) {
        if (value === '' || value === null) {
            // @ts-ignore
            delete postObject[key];
        }
    }
    return postObject;
};

export const getTableConfig = (tableId: QUERY_KEY): TableConfig => {
    const tableConfig = localStorage.getItem(`table-config-${tableId}`);

    return tableConfig
        ? JSON.parse(tableConfig)
        : {
              pagination: initialPagination,
              selectedOperatingFluidFiltersList: [],
              searchTerm: '',
              modelFilterItems: [],
          };
};
export const convertStringArrayToString = (listOfString: string[], separator: string) => {
    if (!listOfString) {
        return '';
    }
    //first remove empty elements, then combine to one string with separator
    return listOfString.filter((item) => item.length > 0).join(separator);
};

export const convertFileToBase64 = (file: File, setFileData: Dispatch<SetStateAction<FileBase46>>) => {
    if (file) {
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === 'string') {
                const fileJson: FileBase46 = {
                    fileName: file.name,
                    fileType: file.type,
                    fileSize: file.size,
                    fileContent: reader.result.split(',')[1],
                };
                setFileData(fileJson);
            }
        };
        reader.onerror = () => {
            console.error('Error reading file');
        };
        reader.readAsDataURL(file);
    }
};

export const convertBase64ToFile = (base64Data: string, fileName: string, fileType: string): File => {
    const byteString = atob(base64Data); // ascii to binary
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
        intArray[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([intArray], { type: fileType });
    return new File([blob], fileName, { type: fileType });
};

const FileTypeMapping: Record<FileType, { mimeType: string; extensions: string[] }> = {
    [FileType.PDF]: { mimeType: 'application/pdf', extensions: ['.pdf'] },
    [FileType.JPEG]: { mimeType: 'image/jpeg', extensions: ['.jpeg', '.jpg'] },
    [FileType.PNG]: { mimeType: 'image/png', extensions: ['.png'] },
    [FileType.GIF]: { mimeType: 'image/gif', extensions: ['.gif'] },
    [FileType.DOC]: { mimeType: 'application/msword', extensions: ['.doc'] },
    [FileType.DOCX]: {
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        extensions: ['.docx'],
    },
    [FileType.DOTX]: {
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
        extensions: ['.dotx'],
    },
    [FileType.PPT]: { mimeType: 'application/vnd.ms-powerpoint', extensions: ['.ppt'] },
    [FileType.PPTX]: {
        mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        extensions: ['.pptx'],
    },
};
export const getFormattedMimeType = (fileType: FileType): { [key: string]: string[] } => {
    const mapping = FileTypeMapping[fileType];
    return { [mapping.mimeType]: mapping.extensions };
};

export const getAllMimeTypes = (): { [key: string]: string[] } => {
    const result: { [key: string]: string[] } = {};

    Object.values(FileType).forEach((fileType) => {
        const { mimeType, extensions } = FileTypeMapping[fileType as FileType];
        result[mimeType] = extensions;
    });

    return result;
};

export const getMimeTypeFiles = (fileTypes: string[]) => {
    const mimeMappping: Accept = getAllMimeTypes();
    const result: Accept = {};
    const normalizedTypes = fileTypes.map((type) => {
        let cleanedType = type.replace(/[^a-zA-Z0-9.]/g, '').toLowerCase();
        if (!cleanedType.startsWith('.')) {
            cleanedType = `.${cleanedType}`;
        }
        return cleanedType;
    });

    const uniqeTyppes = Array.from(new Set(normalizedTypes));
    uniqeTyppes.forEach((type) => {
        for (const [mimeType, extensions] of Object.entries(mimeMappping)) {
            if (extensions.includes(type)) {
                result[mimeType] = result[mimeType] ? [...result[mimeType], type] : [type];
            }
        }
    });
    return result;
};

export const convertDecimalSeparator = (langState: SupportedLanguages, value?: string) => {
    if (!value) {
        return '';
    }

    return langState === SupportedLanguages.de ? value.replace('.', ',') : value.replace(',', '.');
};

export const getHighestVersionOfFiles = (files: VersionedFileMetadataDto[]) => {
    if (files === undefined || files.length === 0) {
        return undefined;
    }

    return files?.reduce((maxItem, currentItem) => {
        return currentItem?.versionNumber && currentItem?.versionNumber > (maxItem?.versionNumber ?? -Infinity)
            ? currentItem
            : maxItem;
    });
};
/**
 * Get the label for a product's state.
 * @param state The state of the product.
 * @returns The label for the product's state.
 * ProductStateMapping has the keys that are in I18nTexts
 */
export const getProductStateLabel = (
    state:
        | ExtendedProductApprovalDto.productApprovalState
        | ProductSheetDto.productSheetState
        | ProductApprovalDto.productApprovalState
        | ProductFormulationDto.formulationState,
    translate: Translate
): string => {
    return translate(ProductStateMapping[state] as keyof I18nTexts);
};
export function arePathsEqual(currentPath: string, targetPath: string) {
    const normalize = (path: string) =>
        path
            .split('/') // divide
            .filter(Boolean) // Remove empty parts (from //)
            .join('/'); // Reassemble

    return normalize(currentPath) === normalize(targetPath);
}

export const getSkipVersionCheckUrl = (url: string) => {
    return url.includes('?') ? `${url}&skipVersionCheck=true` : `${url}?skipVersionCheck=true`;
};
export const getReplaceExistingUrl = (url: string) => {
    return url.includes('?') ? `${url}&replaceExisting=true` : `${url}?replaceExisting=true`;
};
export function getOperatingFluidTypeStringList(
    operatingFluidTypes: OperatingFluidTypeDto[],
    langState: SupportedLanguages
) {
    return operatingFluidTypes.map((type) => ({
        label: langState === 'de' ? type.nameDe : type.nameEn,
        value: type.id ?? '',
    }));
}
export function getSalutationOptions(translate: Translate) {
    return [
        {
            value: AuthorizedSignatoryDto.salutation.MR,
            label: translate('salutation_mr'),
        },
        {
            value: AuthorizedSignatoryDto.salutation.MRS,
            label: translate('salutation_mrs'),
        },
        {
            value: AuthorizedSignatoryDto.salutation.OTHER,
            label: translate('salutation_other'),
        },
    ];
}

export function isEngineOrGearOil(operatingFluidTypeId: string, operatingFluidTypes: OperatingFluidTypeDto[]) {
    const foundType = operatingFluidTypes.find((type) => type.id === operatingFluidTypeId);

    if (!foundType) {
        return false;
    }
    const namesToCheck = [foundType.nameEn.trim().toLowerCase(), foundType.nameDe.trim().toLowerCase()];

    const validNames = new Set(
        [EngineOil.ENGINE_OIL_EN, EngineOil.ENGINE_OIL_DE, GearOil.GEAR_OIL_EN, GearOil.GEAR_OIL_DE].map((v) =>
            v.toLowerCase()
        )
    );
    return namesToCheck.some((name) => validNames.has(name));
}
